import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSuppliers, saveSupplier, exportSuppliersToExcel, searchSuppliers } from "../../components/Api/GmsSupplier";
import { getActiveSupplierCategories } from "../../components/Api/GmsSupplierCategory";
import { Eye, SquarePen, Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import classnames from "classnames";
import Pagination from "rc-pagination";
import "rc-pagination/assets/index.css";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    height: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
    width: '100%',
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '38px',
    padding: '0 8px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    paddingTop: '0px',
    paddingBottom: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '38px',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const getDefaultForm = () => {
  return {
  supplier_Id: 0,
  supplier_Code: "",
  supplier_NameEn: "",
  supplier_NameAr: "",
  supplier_IsInternational: false,
  supplier_AddressEn: "",
  supplier_AddressAr: "",
  supplier_Email: "",
  supplier_ContactNo: "",
  supplier_RepDetails: "",
  supplier_Notes: "",
  supplier_TRN: "",
  supplier_Category_Id: null,
  supplier_Active: true,
  supplier_CreatedDate: new Date().toISOString(),
  supplier_CreatedBy: 1,
  supplier_ModifiedDate: new Date().toISOString(),
  supplier_ModifiedBy: 1,
  };
};

const emptyFilter = {
  supplier_Id: null,
  supplier_Code: "",
  supplier_NameEn: "",
  supplier_NameAr: "",
  supplier_IsInternational: null,
  supplier_AddressEn: "",
  supplier_AddressAr: "",
  supplier_Email: "",
  supplier_ContactNo: "",
  supplier_RepDetails: "",
  supplier_Notes: "",
  supplier_Active: null,
};

const FormInput = ({ label, id, name, type = "text", value, onChange, required = false, placeholder = "", error = "", disabled = false, onKeyPress }) => (
  <div className="form-group mb-3">
    <Label className="col-form-label mb-2" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Input
      type={type}
      className={`form-control ${error ? 'is-invalid' : ''}`}
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      onKeyPress={onKeyPress}
    />
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormSelect = ({ label, id, name, options, value, onChange, error, required = true, disabled = false }) => (
  <div className="form-group mb-3">
    <Label className="col-form-label mb-2" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Input
      type="select"
      className={`form-control ${error ? 'is-invalid' : ''}`}
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      <option value="">Select {label}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Input>
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormCheckbox = ({ label, id, name, checked, onChange, disabled = false }) => (
  <div className="form-group mb-3">
    <div className="form-check form-switch">
      <input
        type="checkbox"
        className="form-check-input"
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <label className="form-check-label" htmlFor={id}>
        {label === "Active" ? (checked ? "Active" : "Inactive") : label}
      </label>
    </div>
  </div>
);

export default function SupplierPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSupplier');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSupplier');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSupplier');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSupplier');
  const isAdmin = isUserAdmin();

  const [suppliers, setSuppliers] = useState([]);
  const [supplierCategories, setSupplierCategories] = useState([]);
  const [form, setForm] = useState(() => {
    const defaultForm = getDefaultForm();
    // Set default status based on permissions - always active for users without delete permission
    if (!hasDeletePermission) {
      defaultForm.supplier_Active = true;
    }
    return defaultForm;
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Supplier search states for Name (English) filter
  const [supplierNameEnSearchOptions, setSupplierNameEnSearchOptions] = useState([]);
  const [supplierNameEnSearchLoading, setSupplierNameEnSearchLoading] = useState(false);
  const [supplierNameEnSearchTimeout, setSupplierNameEnSearchTimeout] = useState(null);
  const [selectedSupplierNameEnOption, setSelectedSupplierNameEnOption] = useState(null);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    if (hasPermission) {
      loadSuppliers();
      loadSupplierCategories();
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab(2); // Show list tab for view-only users
      }
    }
  }, [hasPermission, isViewOnly]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (supplierNameEnSearchTimeout) {
        clearTimeout(supplierNameEnSearchTimeout);
      }
    };
  }, [supplierNameEnSearchTimeout]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await getSuppliers(filter);
      setSuppliers(data);
      setTotalCount(data.length);
    } catch (error) {
      console.error("Error loading suppliers:", error);
      Swal.fire("Error", "Failed to load suppliers", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadSupplierCategories = async () => {
    try {
      const data = await getActiveSupplierCategories();
      // Ensure data is an array
      if (Array.isArray(data)) {
        setSupplierCategories(data);
      } else {
        console.warn("Supplier categories API returned non-array data:", data);
        setSupplierCategories([]);
      }
    } catch (error) {
      console.error("Error loading supplier categories:", error);
      // Set to empty array on error
      setSupplierCategories([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;
    
    // Filter out non-numeric characters for contact number field
    if (name === 'supplier_ContactNo') {
      processedValue = value.replace(/[^0-9]/g, '');
    }
    
    // Handle checkbox - use checked value directly like CustomerPage
    const newValue = type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? null : parseInt(value)) : processedValue);
    
    setForm(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;
    
    // Filter out non-numeric characters for contact number field
    if (name === 'supplier_ContactNo') {
      processedValue = value.replace(/[^0-9]/g, '');
    }
    
    setFilter(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Check if Supplier Category is selected
    if (!form.supplier_Category_Id || form.supplier_Category_Id === null) {
      Swal.fire("Error", "Please select a Supplier Category before saving", "error");
      return;
    }
    
    setLoading(true);
    try {
      const session = getUserSession();
      const result = await saveSupplier({
        ...form,
        supplier_Id: form.supplier_Id === 0 ? null : form.supplier_Id,
        supplier_ModifiedDate: new Date().toISOString(),
        supplier_ModifiedBy: session.userId,
        supplier_CreatedBy: form.supplier_Id === 0 ? session.userId : form.supplier_CreatedBy,
      });
      Swal.fire("Success", result.message || "Supplier saved successfully", "success");
      setForm(getDefaultForm());
      setIsEditing(false);
      loadSuppliers();
    } catch (error) {
      Swal.fire("Error", "Failed to save supplier: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (supplier) => {
    setViewLoadingRow(supplier.supplier_Id);
    try {
      const categoryName = supplier.supplier_Category_Id && Array.isArray(supplierCategories)
        ? supplierCategories.find(cat => cat.supplierCategory_Id === supplier.supplier_Category_Id)?.supplierCategory_NameEn || 'N/A'
        : 'N/A';
      
      Swal.fire({
        title: supplier.supplier_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Name (English):</strong> ${supplier.supplier_NameEn}</p>
              <p><strong>Name (Arabic):</strong> ${supplier.supplier_NameAr}</p>
              <p><strong>Code:</strong> ${supplier.supplier_Code}</p>
              <p><strong>Category:</strong> ${categoryName}</p>
              <p><strong>Email:</strong> ${supplier.supplier_Email}</p>
              <p><strong>Contact No:</strong> ${supplier.supplier_ContactNo}</p>
              <p><strong>TRN:</strong> ${supplier.supplier_TRN || 'N/A'}</p>
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Address</h6>
              <p><strong>Address (English):</strong> ${supplier.supplier_AddressEn}</p>
              <p><strong>Address (Arabic):</strong> ${supplier.supplier_AddressAr || 'N/A'}</p>
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Additional Details</h6>
              <p><strong>International:</strong> ${supplier.supplier_IsInternational ? 'Yes' : 'No'}</p>
              <p><strong>Representative Details:</strong> ${supplier.supplier_RepDetails}</p>
              <p><strong>Notes:</strong> ${supplier.supplier_Notes}</p>
              <p><strong>Status:</strong> ${supplier.supplier_Active ? 'Active' : 'Inactive'}</p>
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Timestamps</h6>
              <p><strong>Created Date:</strong> ${formatFullDateTime(supplier.supplier_CreatedDate)}</p>
              <p><strong>Modified Date:</strong> ${formatFullDateTime(supplier.supplier_ModifiedDate)}</p>
            </div>
          </div>
        `,
        width: '600px',
        showCloseButton: true,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error viewing supplier:", error);
      Swal.fire("Error", "Failed to view supplier details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (supplier) => {
    // Ensure boolean fields are properly converted (in case API returns strings or numbers)
    setForm({
      ...supplier,
      supplier_IsInternational: supplier.supplier_IsInternational === true || supplier.supplier_IsInternational === 1 || supplier.supplier_IsInternational === 'true' || supplier.supplier_IsInternational === '1',
      supplier_Active: supplier.supplier_Active === true || supplier.supplier_Active === 1 || supplier.supplier_Active === 'true' || supplier.supplier_Active === '1',
      supplier_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    setActiveTab(1); // Switch to form tab
  };

  const handleCancelEdit = () => {
    setForm(getDefaultForm());
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    loadSuppliers();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setSelectedSupplierNameEnOption(null);
    setSupplierNameEnSearchOptions([]);
    loadSuppliers();
  };

  // Manual search function for supplier name English filter
  const searchSupplierNameEnManually = async (inputValue) => {
    console.log('🔍 searchSupplierNameEnManually called with inputValue:', inputValue);
    
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      console.log('❌ Input too short, clearing options');
      setSupplierNameEnSearchOptions([]);
      return;
    }
    
    try {
      setSupplierNameEnSearchLoading(true);
      console.log('🚀 Making API call to searchSuppliers with:', { trimmedInput, page: 1, pageSize: 20 });
      const response = await searchSuppliers(trimmedInput, 1, 20);
      console.log('✅ API response received:', response);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        console.log('❌ Invalid response format:', response);
        setSupplierNameEnSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_NameEn || supplier.supplier_NameAr || '',
          label: label,
          supplier: supplier
        };
      });
      
      console.log('📋 Formatted options:', formattedOptions);
      setSupplierNameEnSearchOptions(formattedOptions);
    } catch (error) {
      console.error('❌ Error searching suppliers:', error);
      setSupplierNameEnSearchOptions([]);
    } finally {
      setSupplierNameEnSearchLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.supplier_Id !== null && filter.supplier_Id !== undefined && { supplier_Id: filter.supplier_Id }),
        ...(filter.supplier_Code && filter.supplier_Code.trim() !== '' && { supplier_Code: filter.supplier_Code }),
        ...(filter.supplier_NameEn && filter.supplier_NameEn.trim() !== '' && { supplier_NameEn: filter.supplier_NameEn }),
        ...(filter.supplier_NameAr && filter.supplier_NameAr.trim() !== '' && { supplier_NameAr: filter.supplier_NameAr }),
        ...(filter.supplier_Active !== null && filter.supplier_Active !== undefined && { supplier_Active: filter.supplier_Active })
      };
      
      await exportSuppliersToExcel(cleanFilter);
      Swal.fire("Success", "Supplier report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting suppliers:", error);
      Swal.fire("Error", "Failed to export supplier report", "error");
    } finally {
      setLoading(false);
    }
  };

  const paginatedSuppliers = suppliers.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Supplier Management" breadcrumbItem="GMS" />
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="wizard clearfix">
                  <div className="steps clearfix">
                    <ul>
                      {!isViewOnly && (
                        <NavItem className={classnames({ current: activeTab === 1 })}>
                          <NavLink
                            className={classnames({ current: activeTab === 1 })}
                            onClick={() => setActiveTab(1)}
                          >
                            <span className="number">1.</span> {isEditing ? "Edit Supplier" : "Add Supplier"}
                          </NavLink>
                        </NavItem>
                      )}
                      <NavItem className={classnames({ current: activeTab === 2 })}>
                        <NavLink
                          className={classnames({ active: activeTab === 2 })}
                          onClick={() => setActiveTab(2)}
                        >
                          <span className="number">2.</span> Supplier List
                        </NavLink>
                      </NavItem>
                    </ul>
                        </div>

                  <div className="content clearfix">
                    <TabContent activeTab={activeTab} className="body">
                      <TabPane tabId={1}>
                        <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                              <FormInput
                                label="Supplier Name (English)"
                                id="supplier_NameEn"
                                name="supplier_NameEn"
                          value={form.supplier_NameEn}
                                onChange={handleInputChange}
                                required={true}
                                placeholder="Enter supplier name in English"
                                disabled={isViewOnly}
                              />
                    </Col>
                    <Col md={6}>
                              <FormInput
                                label="Supplier Name (Arabic)"
                                id="supplier_NameAr"
                                name="supplier_NameAr"
                          value={form.supplier_NameAr}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter supplier name in Arabic"
                                disabled={isViewOnly}
                              />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                              <FormInput
                                label="Email"
                                id="supplier_Email"
                                name="supplier_Email"
                          type="email"
                          value={form.supplier_Email}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter email address"
                                disabled={isViewOnly}
                              />
                    </Col>
                    <Col md={6}>
                              <FormInput
                                label="Contact Number"
                                id="supplier_ContactNo"
                                name="supplier_ContactNo"
                          value={form.supplier_ContactNo}
                                onChange={handleInputChange}
                                required={true}
                                placeholder="Enter contact number"
                                disabled={isViewOnly}
                                onKeyPress={(e) => {
                                  // Only allow numbers
                                  const char = String.fromCharCode(e.which);
                                  if (!/[0-9]/.test(char)) {
                                    e.preventDefault();
                                  }
                                }}
                              />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                              <FormInput
                                label="TRN"
                                id="supplier_TRN"
                                name="supplier_TRN"
                          value={form.supplier_TRN}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter TRN"
                                disabled={isViewOnly}
                              />
                    </Col>
                    <Col md={6}>
                              <FormSelect
                                label="Supplier Category"
                                id="supplier_Category_Id"
                                name="supplier_Category_Id"
                                options={Array.isArray(supplierCategories) ? supplierCategories.map(cat => ({
                                  value: cat.supplierCategory_Id,
                                  label: cat.supplierCategory_NameEn
                                })) : []}
                                value={form.supplier_Category_Id || ""}
                                onChange={(e) => {
                                  const value = e.target.value === "" ? null : parseInt(e.target.value);
                                  setForm(prev => ({ ...prev, supplier_Category_Id: value }));
                                }}
                                required={true}
                                disabled={isViewOnly}
                              />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                              <FormInput
                                label="Address (English)"
                                id="supplier_AddressEn"
                                name="supplier_AddressEn"
                          type="textarea"
                          value={form.supplier_AddressEn}
                                onChange={handleInputChange}
                                required={true}
                                placeholder="Enter address in English"
                                disabled={isViewOnly}
                              />
                    </Col>
                    <Col md={6}>
                              <FormInput
                                label="Address (Arabic)"
                                id="supplier_AddressAr"
                                name="supplier_AddressAr"
                          type="textarea"
                          value={form.supplier_AddressAr}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter address in Arabic"
                                disabled={isViewOnly}
                              />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                              <FormInput
                                label="Representative Details"
                                id="supplier_RepDetails"
                                name="supplier_RepDetails"
                          type="textarea"
                          value={form.supplier_RepDetails}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter representative details"
                                disabled={isViewOnly}
                              />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                              <FormInput
                                label="Notes"
                                id="supplier_Notes"
                                name="supplier_Notes"
                          type="textarea"
                          value={form.supplier_Notes}
                                onChange={handleInputChange}
                                required={false}
                                placeholder="Enter additional notes"
                                disabled={isViewOnly}
                              />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                              <FormCheckbox
                                label="Is International"
                                id="supplier_IsInternational"
                                name="supplier_IsInternational"
                                checked={form.supplier_IsInternational}
                                onChange={handleInputChange}
                                disabled={isViewOnly}
                          />
                    </Col>
                  </Row>

                          <Row className="mt-2">
                            {hasDeletePermission && isEditing && (
                              <Col md={6}>
                                <FormCheckbox
                                  label="Is Active"
                                  id="supplier_Active"
                                  name="supplier_Active"
                                  checked={form.supplier_Active}
                                  onChange={handleInputChange}
                                  disabled={isViewOnly}
                                />
                              </Col>
                            )}
                  </Row>

                          <div className="d-flex gap-2 mt-3">
                            <Button type="submit" color="primary" disabled={loading || isViewOnly}>
                      {loading ? (
                        <ClipLoader size={20} color="white" />
                      ) : isEditing ? (
                        "Update Supplier"
                      ) : (
                        "Add Supplier"
                      )}
                    </Button>
                            <Button
                              type="button"
                              color="secondary"
                              onClick={() => {
                                const defaultForm = getDefaultForm();
                                // Set default status based on permissions
                                if (!hasDeletePermission) {
                                  defaultForm.supplier_Active = true;
                                }
                                setForm(defaultForm);
                                setIsEditing(false);
                              }}
                              disabled={isViewOnly}
                            >
                              Clear
                            </Button>
                    {isEditing && (
                      <Button
                        type="button"
                        color="secondary"
                        onClick={handleCancelEdit}
                                disabled={isViewOnly}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </Form>
                      </TabPane>

                      <TabPane tabId={2}>
                        <Row>
                          <Col md={12}>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div className="d-flex align-items-center gap-2">
                                <h5 className="mb-0 fw-bold">Filter Suppliers</h5>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => setShowFilters(!showFilters)}
                                >
                                  {showFilters ? "Hide Filters" : "Show Filters"}
                                </Button>
                              </div>
                              <Button 
                                color="success" 
                                size="sm"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                            </div>

                            {showFilters && (
                              <Card className="mb-3">
                                <CardBody>
                                  <Row>
                                    <Col md={3}>
                                      <FormInput
                                        label="Supplier Code"
                                        id="filter_supplier_Code"
                                        name="supplier_Code"
                                        value={filter.supplier_Code}
                                        onChange={handleFilterChange}
                                        required={false}
                                        placeholder="Filter by supplier code"
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <div className="form-group mb-3">
                                        <Label className="col-form-label mb-2">Name (English)</Label>
                                        <Select
                                          value={selectedSupplierNameEnOption}
                                          onChange={(option) => {
                                            setSelectedSupplierNameEnOption(option);
                                            setFilter({ ...filter, supplier_NameEn: option?.value || "" });
                                          }}
                                          options={supplierNameEnSearchOptions}
                                          placeholder="Search by name..."
                                          styles={selectStyles}
                                          isClearable
                                          isSearchable
                                          isLoading={supplierNameEnSearchLoading}
                                          noOptionsMessage={() => "Type at least 3 characters"}
                                          loadingMessage={() => "Searching..."}
                                          filterOption={null}
                                          onInputChange={(inputValue) => {
                                            console.log('🔤 Filter Input changed:', inputValue);
                                            
                                            // Clear previous timeout
                                            if (supplierNameEnSearchTimeout) {
                                              clearTimeout(supplierNameEnSearchTimeout);
                                            }
                                            
                                            // Trim the input value before searching
                                            const trimmedValue = inputValue ? inputValue.trim() : '';
                                            
                                            // Set new timeout
                                            const timeout = setTimeout(() => {
                                              if (trimmedValue.length >= 3) {
                                                searchSupplierNameEnManually(trimmedValue);
                                              } else {
                                                setSupplierNameEnSearchOptions([]);
                                              }
                                            }, 300);
                                            setSupplierNameEnSearchTimeout(timeout);
                                            
                                            return inputValue;
                                          }}
                                        />
                                      </div>
                                    </Col>
                                    {/* <Col md={3}>
                                      <FormInput
                                        label="Name (Arabic)"
                                        id="filter_supplier_NameAr"
                                        name="supplier_NameAr"
                                        value={filter.supplier_NameAr}
                                        onChange={handleFilterChange}
                                        required={false}
                                        placeholder="Filter by Arabic name"
                                      />
                                    </Col> */}
                                    <Col md={3}>
                                      <FormInput
                                        label="Email"
                                        id="filter_supplier_Email"
                                        name="supplier_Email"
                                        value={filter.supplier_Email}
                                        onChange={handleFilterChange}
                                        required={false}
                                        placeholder="Filter by email"
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mt-3">
                                    <Col md={3}>
                                      <FormInput
                                        label="Contact Number"
                                        id="filter_supplier_ContactNo"
                                        name="supplier_ContactNo"
                                        value={filter.supplier_ContactNo}
                                        onChange={handleFilterChange}
                                        required={false}
                                        placeholder="Filter by contact number"
                                        onKeyPress={(e) => {
                                          // Only allow numbers
                                          const char = String.fromCharCode(e.which);
                                          if (!/[0-9]/.test(char)) {
                                            e.preventDefault();
                                          }
                                        }}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <FormSelect
                                        label="International"
                                        id="filter_supplier_IsInternational"
                                        name="supplier_IsInternational"
                                        options={[
                                          { value: true, label: "Yes" },
                                          { value: false, label: "No" }
                                        ]}
                                        value={filter.supplier_IsInternational}
                                        onChange={handleFilterChange}
                                        required={false}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <FormSelect
                                        label="Status"
                                        id="filter_supplier_Active"
                                        name="supplier_Active"
                                        options={[
                                          { value: true, label: "Active" },
                                          { value: false, label: "Inactive" }
                                        ]}
                                        value={filter.supplier_Active}
                                        onChange={handleFilterChange}
                                        required={false}
                                      />
                                    </Col>
                                    <Col md={3}>
                                      <div className="form-group mb-3">
                                        <Label className="col-form-label mb-2" style={{ visibility: 'hidden' }}>Actions</Label>
                                        <div className="d-flex gap-2">
                                          <Button color="primary" size="sm" onClick={handleApplyFilter}>
                                            Apply Filters
                                          </Button>
                                          <Button color="secondary" size="sm" onClick={handleClearFilter}>
                                            Clear Filters
                                          </Button>
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            )}

                            <Card>
                              <CardBody>
                                <div className="table-responsive">
                                  <table className="table table-striped table-bordered">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Code</th>
                                        <th>Name (En)</th>
                                        <th>Name (Ar)</th>
                                        <th>Category</th>
                                        <th>Email</th>
                                        <th>Contact No</th>
                                        <th>TRN</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {loading ? (
                                        <tr>
                                          <td colSpan="10" className="text-center">
                                            <ClipLoader size={30} color="#556ee6" />
                                          </td>
                                        </tr>
                                      ) : paginatedSuppliers.length === 0 ? (
                                        <tr>
                                          <td colSpan="10" className="text-center">No suppliers found</td>
                                        </tr>
                                      ) : (
                                        paginatedSuppliers.map((supplier, index) => {
                                          const categoryName = supplier.supplier_Category_Id && Array.isArray(supplierCategories)
                                            ? supplierCategories.find(cat => cat.supplierCategory_Id === supplier.supplier_Category_Id)?.supplierCategory_NameEn || 'N/A'
                                            : 'N/A';
                                          return (
                                          <tr key={supplier.supplier_Id}>
                                            <td>{(page - 1) * pageSize + index + 1}</td>
                                            <td>{supplier.supplier_Code}</td>
                                            <td>{supplier.supplier_NameEn}</td>
                                            <td>{supplier.supplier_NameAr}</td>
                                            <td>{categoryName}</td>
                                            <td>{supplier.supplier_Email}</td>
                                            <td>{supplier.supplier_ContactNo}</td>
                                            <td>{supplier.supplier_TRN || 'N/A'}</td>
                                            <td>
                                              <span className={`badge ${supplier.supplier_Active ? 'bg-success' : 'bg-danger'}`}>
                                                {supplier.supplier_Active ? 'Active' : 'Inactive'}
                                              </span>
                                            </td>
                                            <td>
                                              <div className="d-flex gap-2">
                                                <Button
                                                  color="info"
                                                  size="sm"
                                                  onClick={() => handleView(supplier)}
                                                  disabled={viewLoadingRow === supplier.supplier_Id}
                                                >
                                                  {viewLoadingRow === supplier.supplier_Id ? (
                                                    <ClipLoader size={15} color="white" />
                                                  ) : (
                                                    <Eye size={15} />
                                                  )}
                                                </Button>
                                                {hasEditOrHigher && (
                                                  <Button
                                                    color="warning"
                                                    size="sm"
                                                    onClick={() => handleEdit(supplier)}
                                                    disabled={editLoadingRow === supplier.supplier_Id}
                                                  >
                                                    {editLoadingRow === supplier.supplier_Id ? (
                                                      <ClipLoader size={15} color="white" />
                                                    ) : (
                                                      <SquarePen size={15} />
                                                    )}
                                                  </Button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                          );
                                        })
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {totalCount > 0 && (
                                  <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                      <p className="text-muted mb-0">
                                        Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                      </p>
                                    </div>
                                    <div className="pagination-wrapper">
                                      <Pagination
                                        className="ant-pagination"
                                        current={page}
                                        total={totalCount}
                                        pageSize={pageSize}
                                        onChange={(pageNum) => setPage(pageNum)}
                                        showTitle={false}
                                      />
                                    </div>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>
                    </TabContent>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 